import { useState } from 'react';
import API from '../../../api/API';
import { createMatchData } from '../../match/utils/matchUtils';

export const useRefertoAPI = () => {

  // Validate hash and get teams data for referto
  const loadRefertoData = async (s1, s2, hash) => {
    try {
      const response = await API.partite.referto(s1, s2, hash);
      
      if (!response || !response.isSuccess) {
        throw new Error('Codice di sicurezza non valido');
      }

      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Errore nel caricamento del referto:', error);
      return {
        success: false,
        message: error.message || 'Errore nel caricamento dei dati'
      };
    }
  };

  // Load existing match statistics if available
  const loadExistingStatistics = async (squadraAId, squadraBId, nome = null) => {
    try {
      const response = await API.partite.squadre(squadraAId, squadraBId);

      if (!response || !response.isSuccess || !response.data) {
        return null;
      }

      if (response.data.length === 0) return null;
      if (nome && response.data.length > 1) {
        const match = response.data.find(p => p.nome === nome);
        if (match) return match;
      }
      return response.data[0];
    } catch (error) {
      console.error('Errore nel caricamento delle statistiche:', error);
      return null;
    }
  };

  // Convert existing match statistics to referto format
  const convertMatchToRefertoFormat = (matchData, setRefertoData, squadraA, squadraB) => {
    if (!matchData || !matchData.statistiche) return;

    const statistiche = matchData.statistiche;
    // ?? e non ||: uno zero e' un voto assegnato e deve tornare nella casella, mentre || lo
    // scambierebbe per un voto mai raccolto
    const newRefertoData = {
      squadraA: {
        fairPlay: matchData.fairPlay1 ?? '',
        mete: {},
        scalpi1: {},
        scalpi2: {},
        scalpi3: {},
        scalpiBuoni: {},
        assist: {},
        vuotiSquadra: {},
        falliSquadra: {},
        meteTotali: {}
      },
      squadraB: {
        fairPlay: matchData.fairPlay2 ?? '',
        mete: {},
        scalpi1: {},
        scalpi2: {},
        scalpi3: {},
        scalpiBuoni: {},
        assist: {},
        vuotiSquadra: {},
        falliSquadra: {},
        meteTotali: {}
      },
      girone: '',
      campo: '',
      ore: '',
      note: ''
    };

    // Convert player statistics to referto format
    squadraA.giocatori.forEach((player) => {
      const playerStats = statistiche.find(stat => stat.idP === player.idP);
      if (playerStats) {
        // Convert mete
        for (let i = 1; i <= (playerStats.mete || 0); i++) {
          newRefertoData.squadraA.mete[`${player.idP}_${i}`] = '1';
        }
        
        // Convert pieni (scalpi buoni)
        for (let i = 1; i <= (playerStats.pieni || 0); i++) {
          newRefertoData.squadraA.scalpiBuoni[`${player.idP}_${i}`] = '1';
        }
        
        // Convert vuoti (scalpi vuoti) - map each group to its separate section
        // Group 1: scalpi1 (columns 1-3)
        for (let i = 1; i <= (playerStats.vuoti1 || 0); i++) {
          newRefertoData.squadraA.scalpi1[`${player.idP}_${i}`] = '1';
        }
        // Group 2: scalpi2 (columns 1-3)
        for (let i = 1; i <= (playerStats.vuoti2 || 0); i++) {
          newRefertoData.squadraA.scalpi2[`${player.idP}_${i}`] = '1';
        }
        // Group 3: scalpi3 (columns 1-2)
        for (let i = 1; i <= (playerStats.vuoti3 || 0); i++) {
          newRefertoData.squadraA.scalpi3[`${player.idP}_${i}`] = '1';
        }
        
        // Convert assist
        for (let i = 1; i <= (playerStats.assist || 0); i++) {
          newRefertoData.squadraA.assist[`${player.idP}_${i}`] = '1';
        }
      }
    });

    squadraB.giocatori.forEach((player) => {
      const playerStats = statistiche.find(stat => stat.idP === player.idP);
      if (playerStats) {
        // Convert mete
        for (let i = 1; i <= (playerStats.mete || 0); i++) {
          newRefertoData.squadraB.mete[`${player.idP}_${i}`] = '1';
        }
        
        // Convert pieni (scalpi buoni)
        for (let i = 1; i <= (playerStats.pieni || 0); i++) {
          newRefertoData.squadraB.scalpiBuoni[`${player.idP}_${i}`] = '1';
        }
        
        // Convert vuoti (scalpi vuoti) - map each group to its separate section
        // Group 1: scalpi1 (columns 1-3)
        for (let i = 1; i <= (playerStats.vuoti1 || 0); i++) {
          newRefertoData.squadraB.scalpi1[`${player.idP}_${i}`] = '1';
        }
        // Group 2: scalpi2 (columns 1-3)
        for (let i = 1; i <= (playerStats.vuoti2 || 0); i++) {
          newRefertoData.squadraB.scalpi2[`${player.idP}_${i}`] = '1';
        }
        // Group 3: scalpi3 (columns 1-2)
        for (let i = 1; i <= (playerStats.vuoti3 || 0); i++) {
          newRefertoData.squadraB.scalpi3[`${player.idP}_${i}`] = '1';
        }
        
        // Convert assist
        for (let i = 1; i <= (playerStats.assist || 0); i++) {
          newRefertoData.squadraB.assist[`${player.idP}_${i}`] = '1';
        }
      }
    });

    // Convert team fouls
    if (matchData.falli1) {
      for (let i = 1; i <= matchData.falli1; i++) {
        newRefertoData.squadraA.falliSquadra[i] = '1';
      }
    }
    
    if (matchData.falli2) {
      for (let i = 1; i <= matchData.falli2; i++) {
        newRefertoData.squadraB.falliSquadra[i] = '1';
      }
    }

    setRefertoData(prev => ({ ...prev, ...newRefertoData }));
  };

  // Save referto data as match
  const salvaReferto = async (formData) => {
    try {
      // Convert referto data to match format
      const matchStats = formData.convertToMatchFormat();
      
      const partitaData = createMatchData(
        formData.isEditMode || false,
        formData.partitaCorrente,
        formData.edizioneSelezionata,
        formData.squadraA,
        formData.squadraB,
        matchStats.giocatoriSquadraA,
        matchStats.giocatoriSquadraB,
        matchStats.falli1,
        matchStats.falli2,
        matchStats.fairPlay1,
        matchStats.fairPlay2,
        formData.tipoPartita || 'girone',
        formData.fasciaPartita || 'prima',
        formData.partitaCorrente?.nome
      );

      let response;
      if (formData.isEditMode) {
        response = await API.partite.update(partitaData);
      } else {
        response = await API.partite.create(partitaData);
      }

      if (response && response.isSuccess) {
        return {
          success: true,
          data: response.data,
          message: formData.isEditMode ? 'Referto aggiornato con successo!' : 'Referto salvato con successo!'
        };
      } else {
        console.error('API call failed:', response);
        return {
          success: false,
          message: `Errore durante il salvataggio del referto: ${response?.errorMsg || 'Unknown error'}`
        };
      }
    } catch (error) {
      console.error('Errore durante il salvataggio:', error);
      return {
        success: false,
        message: 'Errore durante il salvataggio del referto'
      };
    }
  };

  // Auto-update match data (handles both create and update)
  const autoUpdateMatch = async (formData) => {
    if (!formData.partitaCorrente) {
      return { success: false, message: 'Partita non trovata' };
    }

    try {
      const matchStats = formData.convertToMatchFormat();
      const isUpdate = !!formData.partitaCorrente.id;
      
      const partitaData = createMatchData(
        isUpdate,
        formData.partitaCorrente,
        formData.edizioneSelezionata,
        formData.squadraA,
        formData.squadraB,
        matchStats.giocatoriSquadraA,
        matchStats.giocatoriSquadraB,
        matchStats.falli1,
        matchStats.falli2,
        matchStats.fairPlay1,
        matchStats.fairPlay2,
        formData.tipoPartita || 'girone',
        formData.fasciaPartita || 'prima',
        formData.partitaCorrente?.nome
      );
      

      let response;
      if (isUpdate) {
        response = await API.partite.update(partitaData);
      } else {
        response = await API.partite.create(partitaData);
      }

      if (response && response.isSuccess) {
        return {
          success: true,
          data: response.data,
          isNewMatch: !isUpdate
        };
      } else {
        return {
          success: false,
          message: `Errore durante ${isUpdate ? 'l\'aggiornamento' : 'il salvataggio'}: ${response?.errorMsg || 'Unknown error'}`
        };
      }
    } catch (error) {
      console.error('Errore durante l\'auto-save:', error);
      return {
        success: false,
        message: 'Errore durante l\'auto-save'
      };
    }
  };

  return {
    loadRefertoData,
    loadExistingStatistics,
    convertMatchToRefertoFormat,
    salvaReferto,
    autoUpdateMatch
  };
};